import type { DetailedInvoiceRow } from '@/api/main/types.gen'
import { Lenient, toNumber } from './billingForecast'

export type InvoiceRowInput = Lenient<DetailedInvoiceRow>

export interface DistrictBreakdownRow {
  district_code: string
  /** Monitoring amount billed so far (CZK), same composition as the backend's `kc_monitoring`. */
  billed_czk: number
  km_billed: number
  km_ordered: number
  /** Billed / ordered km; null when nothing was ordered. */
  fulfilment: number | null
  unreliable_km_unbilled: number
  km_missing_billable_overhead: number
  km_potentially_billable_overhead: number
}

const KM_KINDS = ['scan', 'stat', 'enf', 'doc', 'spec', 'tst'] as const
const COST_FIELDS = ['kc_kov', 'kc_puv', 'kc_put', 'kc_enf', 'kc_stat', 'kc_doc', 'kc_spec', 'kc_tst'] as const

/** Sum invoice rows (one per subdistrict and requirement) into one row per district. */
export const aggregateInvoiceRowsByDistrict = (rows: InvoiceRowInput[]): DistrictBreakdownRow[] => {
  const byDistrict = new Map<string, DistrictBreakdownRow>()

  for (const row of rows) {
    const item = byDistrict.get(row.district_code) ?? {
      district_code: row.district_code,
      billed_czk: 0,
      km_billed: 0,
      km_ordered: 0,
      fulfilment: null,
      unreliable_km_unbilled: 0,
      km_missing_billable_overhead: 0,
      km_potentially_billable_overhead: 0,
    }

    item.billed_czk += COST_FIELDS.reduce((sum, field) => sum + toNumber(row[field]), 0)
    item.km_billed += KM_KINDS.reduce((sum, kind) => sum + toNumber(row[`km_${kind}`]), 0)
    item.km_ordered += KM_KINDS.reduce((sum, kind) => sum + toNumber(row[`km_${kind}_ordered`]), 0)
    item.unreliable_km_unbilled += toNumber(row.unreliable_km_unbilled)
    item.km_missing_billable_overhead += toNumber(row.km_missing_billable_overhead)
    item.km_potentially_billable_overhead += toNumber(row.km_potentially_billable_overhead)

    byDistrict.set(row.district_code, item)
  }

  return [...byDistrict.values()].map(item => ({
    ...item,
    fulfilment: item.km_ordered > 0 ? item.km_billed / item.km_ordered : null,
  }))
}
